import React, { memo } from "react";

import { normalizeProductImage } from "../../../../utils/productImages";

export const SHOP_KEY = "ready-made";

const DRAFT_SCHEMA_VERSION = 4;
const DRAFT_TTL_MS = 1000 * 60 * 60 * 24 * 14;

export const DRAFT_WRITE_DELAY_MS = 400;

export const FLAG_FIELDS = [
  "exclusive",
  "limitedEdition",
  "isVintage",
  "isCustomMade",
  "isNewArrival",
  "isEditorsPick",
  "isTrending",
  "isFeatured",
];

export const FIELD_FLAG_LABELS = {
  exclusive: "Exclusive",
  limitedEdition: "Limited edition",
  isVintage: "Vintage",
  isCustomMade: "Custom made",
  isNewArrival: "New arrival",
  isEditorsPick: "Editor's pick",
  isTrending: "Trending",
  isFeatured: "Featured",
};

export const GENDER_OPTIONS = [
  { value: "", label: "Unspecified" },
  { value: "women", label: "Women" },
  { value: "men", label: "Men" },
  { value: "unisex", label: "Unisex" },
  { value: "kids", label: "Kids" },
];

export const DEFAULT_VARIANT_TYPE = "standard";

export function safeText(value = "") {
  return String(value ?? "").trim();
}

export function normalizeLower(value = "") {
  return safeText(value).toLowerCase();
}

export function normalizeSku(value = "") {
  return safeText(value).toUpperCase();
}

export function normalizeGender(value = "") {
  const next = normalizeLower(value);
  return GENDER_OPTIONS.some((option) => option.value === next) ? next : "";
}

export function getIdValue(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value?._id || value?.id || "";
}

export function getProductId(product = null) {
  return getIdValue(product);
}

export function makeClientId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function splitTokens(value = "") {
  return [
    ...new Set(
      String(value || "")
        .split(/[\n,]+/)
        .map((item) => normalizeLower(item))
        .filter(Boolean),
    ),
  ];
}

export function joinTokens(values = [], fallback = "") {
  if (Array.isArray(values) && values.length) {
    return values.map(safeText).filter(Boolean).join(", ");
  }

  return safeText(fallback);
}

export function toNumberOrZero(value) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : 0;
}

export function toIntegerOrZero(value) {
  const number = Math.floor(Number(value));
  return Number.isFinite(number) && number >= 0 ? number : 0;
}

export function hasPositiveNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0;
}

export function normalizeImage(image) {
  return normalizeProductImage(image);
}

export function normalizeImageArray(images = []) {
  const raw = Array.isArray(images) ? images : images ? [images] : [];
  return raw.map(normalizeImage).filter(Boolean);
}

export function buildFlagState(product = null) {
  return FLAG_FIELDS.reduce((acc, key) => {
    acc[key] = Boolean(product?.[key]);
    return acc;
  }, {});
}

export function buildVariantRow(variant = {}) {
  return {
    _clientId: variant?._clientId || makeClientId(),

    name: safeText(variant?.name),
    shortDescription: safeText(variant?.shortDescription),
    sku: normalizeSku(variant?.sku),

    variantType: safeText(variant?.variantType) || DEFAULT_VARIANT_TYPE,

    price:
      variant?.price === undefined || variant?.price === null
        ? ""
        : String(variant.price),

    countInStock:
      variant?.countInStock === undefined || variant?.countInStock === null
        ? ""
        : String(variant.countInStock),

    countReserved:
      variant?.countReserved === undefined || variant?.countReserved === null
        ? "0"
        : String(variant.countReserved),

    itemsPerPack:
      variant?.itemsPerPack === undefined || variant?.itemsPerPack === null
        ? "1"
        : String(variant.itemsPerPack),

    unitLabel: safeText(variant?.unitLabel),

    color: safeText(variant?.color),
    size: safeText(variant?.size),
    material: safeText(variant?.material),
    length: safeText(variant?.length),

    images: normalizeImageArray(variant?.images ?? variant?.image),
  };
}

export function getDraftKey(product = null) {
  return `ready-made-product-form:${getProductId(product) || "new"}`;
}

function getLocalStorage() {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage || null;
  } catch {
    return null;
  }
}

export function readDraft(key) {
  const storage = getLocalStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw);

    if (parsed?.schemaVersion !== DRAFT_SCHEMA_VERSION) return null;

    if (!parsed?.savedAt || Date.now() - parsed.savedAt > DRAFT_TTL_MS) {
      storage.removeItem(key);
      return null;
    }

    return parsed.data || null;
  } catch {
    return null;
  }
}

export function writeDraft(key, value) {
  const storage = getLocalStorage();
  if (!storage) return;

  try {
    storage.setItem(
      key,
      JSON.stringify({
        schemaVersion: DRAFT_SCHEMA_VERSION,
        savedAt: Date.now(),
        data: value,
      }),
    );
  } catch {
    // Ignore quota/security errors.
  }
}

export function clearDraft(key) {
  const storage = getLocalStorage();
  if (!storage) return;

  try {
    storage.removeItem(key);
  } catch {
    // Ignore storage errors.
  }
}

export function mergeById(items = []) {
  const map = new Map();

  for (const item of items) {
    const id = getIdValue(item);
    if (id) map.set(id, item);
  }

  return [...map.values()];
}

export function buildInitialState(product = null, draft = null) {
  const source = draft || product || {};
  const readyMade = source.readyMade || product?.readyMade || {};
  const madeToMeasure = source.madeToMeasure || product?.madeToMeasure || {};

  const productVariants =
    product?.isVariant && Array.isArray(product?.variants)
      ? product.variants
      : [];

  const draftVariants =
    Array.isArray(draft?.variants) && draft.variants.length
      ? draft.variants
      : null;

  return {
    category: getIdValue(source.category || product?.category),
    subcategory: getIdValue(
      source.subCategory ||
        source.subcategory ||
        product?.subCategory ||
        product?.subcategory,
    ),

    name: safeText(source.name),
    brand: safeText(source.brand),
    description: safeText(source.description),

    sku: normalizeSku(source.sku || product?.sku),

    isVariant: Boolean(source.isVariant),

    price:
      source.price === undefined || source.price === null
        ? product?.isVariant
          ? ""
          : (product?.price ?? "")
        : String(source.price),

    countInStock:
      source.countInStock === undefined || source.countInStock === null
        ? product?.isVariant
          ? ""
          : (product?.countInStock ?? "")
        : String(source.countInStock),

    countReserved:
      source.countReserved === undefined || source.countReserved === null
        ? product?.isVariant
          ? "0"
          : (product?.countReserved ?? "0")
        : String(source.countReserved),

    itemsPerPack:
      source.itemsPerPack === undefined || source.itemsPerPack === null
        ? "1"
        : String(source.itemsPerPack),

    unitLabel: safeText(source.unitLabel || product?.unitLabel),

    colorsText:
      source.colorsText ||
      joinTokens(source.colors, source.color) ||
      joinTokens(product?.colors, product?.color),

    sizesText:
      source.sizesText ||
      joinTokens(source.sizes, source.size) ||
      joinTokens(product?.sizes, product?.size),

    materialsText:
      source.materialsText ||
      joinTokens(source.materials, source.material) ||
      joinTokens(product?.materials, product?.material),

    patternsText:
      source.patternsText ||
      joinTokens(source.patterns) ||
      joinTokens(product?.patterns),

    tagsText:
      source.tagsText || joinTokens(source.tags) || joinTokens(product?.tags),

    groupsText:
      source.groupsText ||
      joinTokens(source.groups) ||
      joinTokens(product?.groups),

    origin: safeText(source.origin || product?.origin),
    usage: safeText(source.usage || product?.usage),
    careInstructions: safeText(
      source.careInstructions || product?.careInstructions,
    ),

    gender: normalizeGender(readyMade.gender),
    era: safeText(readyMade.era),
    fit: safeText(readyMade.fit),
    silhouette: safeText(readyMade.silhouette),
    closure: safeText(readyMade.closure),

    madeToMeasureEligible: Boolean(madeToMeasure.eligibleAsDesign),
    madeToMeasureSuitabilityNotes: safeText(madeToMeasure.suitabilityNotes),

    images: normalizeImageArray(source.images ?? product?.images),

    ...buildFlagState(source || product),

    variants: draftVariants
      ? draftVariants.map(buildVariantRow)
      : productVariants.length
        ? productVariants.map(buildVariantRow)
        : [buildVariantRow()],
  };
}

export function validateForm(form) {
  const category = safeText(form.category);
  const name = safeText(form.name);
  const description = safeText(form.description);
  const sku = normalizeSku(form.sku);

  if (!category) {
    return "Please select a category before saving this ready-made product.";
  }

  if (!name) return "Product name is required.";
  if (!description) return "Product description is required.";

  if (!form.isVariant) {
    if (!sku) return "SKU is required for simple ready-made products.";

    if (!hasPositiveNumber(form.price)) {
      return "Price must be greater than 0.";
    }

    if (
      toIntegerOrZero(form.countReserved) > toIntegerOrZero(form.countInStock)
    ) {
      return "Reserved stock cannot exceed stock.";
    }

    return "";
  }

  const variants = Array.isArray(form.variants) ? form.variants : [];
  if (!variants.length) return "Add at least one variant.";

  const skuSet = new Set();

  for (const variant of variants) {
    const variantSku = normalizeSku(variant.sku);

    if (!variantSku) return "Every variant must have a SKU.";

    if (skuSet.has(variantSku)) {
      return "Variant SKUs must be unique within this product.";
    }

    skuSet.add(variantSku);

    if (!hasPositiveNumber(variant.price)) {
      return `Price is required for variant ${variantSku}.`;
    }

    if (
      toIntegerOrZero(variant.countReserved) >
      toIntegerOrZero(variant.countInStock)
    ) {
      return `Reserved stock cannot exceed stock for variant ${variantSku}.`;
    }
  }

  return "";
}

export function buildPayload(form) {
  const colors = splitTokens(form.colorsText);
  const sizes = splitTokens(form.sizesText);
  const materials = splitTokens(form.materialsText);
  const patterns = splitTokens(form.patternsText);
  const tags = splitTokens(form.tagsText);
  const groups = splitTokens(form.groupsText);

  const flags = FLAG_FIELDS.reduce((acc, key) => {
    acc[key] = Boolean(form[key]);
    return acc;
  }, {});

  const payload = {
    shopKey: safeText(form.shopKey) || SHOP_KEY,

    category: safeText(form.category),
    subCategory: safeText(form.subcategory) || null,

    name: safeText(form.name),
    brand: safeText(form.brand),
    description: safeText(form.description),

    isVariant: Boolean(form.isVariant),

    images: normalizeImageArray(form.images),

    color: colors[0] || "",
    colors,

    size: sizes[0] || "",
    sizes,

    material: materials[0] || "",
    materials,

    pattern: patterns[0] || "",
    patterns,

    tags,
    groups,

    origin: safeText(form.origin),
    usage: safeText(form.usage),
    careInstructions: safeText(form.careInstructions),

    readyMade: {
      gender: normalizeGender(form.gender),
      era: safeText(form.era),
      fit: safeText(form.fit),
      silhouette: safeText(form.silhouette),
      closure: safeText(form.closure),
    },

    madeToMeasure: {
      eligibleAsDesign: Boolean(form.madeToMeasureEligible),
      eligibleAsTextile: false,
      eligibleAsHaberdashery: false,
      suitabilityNotes: safeText(form.madeToMeasureSuitabilityNotes),
    },

    ...flags,
  };

  if (form.isVariant) {
    payload.sku = undefined;
    payload.price = 0;
    payload.countInStock = 0;
    payload.countReserved = 0;
    payload.itemsPerPack = 1;
    payload.unitLabel = "";

    payload.variants = form.variants.map((variant) => ({
      name: safeText(variant.name),
      shortDescription: safeText(variant.shortDescription),
      sku: normalizeSku(variant.sku),
      variantType: DEFAULT_VARIANT_TYPE,

      price: toNumberOrZero(variant.price),

      countInStock: toIntegerOrZero(variant.countInStock),
      countReserved: toIntegerOrZero(variant.countReserved),

      lengthInStock: 0,
      lengthReserved: 0,

      unitValue: 1,
      unitLabel: safeText(variant.unitLabel),
      itemsPerPack: Math.max(1, toIntegerOrZero(variant.itemsPerPack) || 1),

      color: normalizeLower(variant.color),
      size: normalizeLower(variant.size),
      material: normalizeLower(variant.material),
      length: normalizeLower(variant.length),

      images: normalizeImageArray(variant.images),
    }));

    return payload;
  }

  payload.sku = normalizeSku(form.sku);
  payload.price = toNumberOrZero(form.price);
  payload.countInStock = toIntegerOrZero(form.countInStock);
  payload.countReserved = toIntegerOrZero(form.countReserved);
  payload.itemsPerPack = Math.max(1, toIntegerOrZero(form.itemsPerPack) || 1);
  payload.unitLabel = safeText(form.unitLabel);
  payload.variants = [];

  return payload;
}

export const Field = memo(function Field({
  label,
  children,
  hint = "",
  error = "",
}) {
  return (
    <label className="admin-field">
      {label ? <span className="admin-field__label">{label}</span> : null}
      {children}
      {hint ? <span className="admin-field__hint">{hint}</span> : null}
      {error ? <span className="admin-field__error">{error}</span> : null}
    </label>
  );
});

export const Section = memo(function Section({ title, hint = "", children }) {
  return (
    <section className="admin-form-section">
      {title || hint ? (
        <div className="admin-form-section__header">
          {title ? (
            <h3 className="admin-form-section__title">{title}</h3>
          ) : null}
          {hint ? <p className="admin-form-section__hint">{hint}</p> : null}
        </div>
      ) : null}

      {children}
    </section>
  );
});
